import { useLocation } from 'react-router-dom'
import { useAppSelector } from '../../common/store'
import { getLaiskeyFromPath } from '../../common/util'
import { getAPData, getAPFrameData, getAPProjectDetails } from './ap.slice'
import { APFrameDetailsType } from './ap.types'
import {AP_IMAGE_PATH, LAYER_AERIAL_PHOTOGRAPHY_PATH, RECAPTCHA_ENABLED} from "../../environment";
import {selectSelectedFrame} from "./apFrames.slice";
import {useEffect, useState} from "react";
import {generateRecaptchaToken} from "../../common/recapctcha";
import APFrameDetailsPanel from "./APFrameDetailsPanel";

const useAPData = () => {
  const location = useLocation()
  const laisKey = getLaiskeyFromPath(location.pathname, true)
  const apData = useAppSelector(getAPData)

  if (apData && apData.parcelData) {
    if (apData.parcelData.length > 0) {
      // there are a couple of records in ILIS that have more than one parcel for a single
      // LAISKEY, f.e. '200     07414  ' and '200     07840  '.
      // We return the first one in this instance
      if (apData.parcelData[0].hasOwnProperty('laisKey') && apData.parcelData[0].laisKey === laisKey) {
        return apData.parcelData[0]
      }
    } else {
      // this is an error, this means we've got a valid laiskey but there is no parcel data.
      // more than likely the parcel is proposed and/or administrative, and we don't have any details
      // for it in the parcel table
      console.log('ERROR: No parcel details found for \'' + laisKey + '\'')
    }
  }

  return null
}

type imgSrcUrlProps = {
  selectedFrameDetails: APFrameDetailsType,
  width: number,
  height: number,
  quality: number,
  recaptchaToken: string
}

const imgSrcUrl = (props: imgSrcUrlProps) => {
  const selectedFrameDetails = props.selectedFrameDetails
  const width = props.width || 260
  const height = props.height || 260
  const quality = props.quality || 100
  const recaptchaV3Key = props.recaptchaToken || ""

  let url = ""

  if (selectedFrameDetails) {
    let ext = 'jp2'
    let filmType = selectedFrameDetails.camera
    let year = selectedFrameDetails.year
    let frameId = selectedFrameDetails.frameIdentifier

    if (selectedFrameDetails.file_ext) {
      ext = selectedFrameDetails.file_ext
    } else {
      // try to guess what the file ext should be based on the film type, this can't be 100% foolproof
      if (filmType === 'Digital' && year > 2020) {
        ext = 'tif'
      }
    }
    url = `${AP_IMAGE_PATH}?year=${year}&frameIdentifier=${frameId}&extension=${ext}&width=${width}&height=${height}&quality=${quality}`
    if (RECAPTCHA_ENABLED && recaptchaV3Key.length > 0) {
      url = url + `&recaptchaV3Key=${recaptchaV3Key}`
    }
  }
  return url
}

const useAPFrameData = () => {
  const apFrameData = useAppSelector(getAPFrameData)

  if (apFrameData) {
    return apFrameData
  }

  return null
}

const useAPProjectData = () => {
  const apProjectData = useAppSelector(getAPProjectDetails)

  if (apProjectData) {
    return apProjectData
  }

  return null
}

const numberFormatter = new Intl.NumberFormat('en-AU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const getYearsArrayFromFrames = (frames: APFrameDetailsType[]) => {
  // Available years come from the full set of frames
  const yearsSet = new Set<number>()
  frames.map((row) => yearsSet.add(row.year))
  return [...yearsSet]
}

const getFramesForYear = (frames: APFrameDetailsType[], year: number) => {
  return frames.filter(data => data.year === year)
}

const buildCoordinateSearchPath = ( longitude: number, latitude: number, radius: number) => {
  return `${LAYER_AERIAL_PHOTOGRAPHY_PATH}/coord:${latitude},${longitude},${radius}`
}

export const APUtils = {
  useAPData,
  useAPFrameData,
  useAPProjectData,
  numberFormatter,
  getYearsArrayFromFrames,
  getFramesForYear,
  buildCoordinateSearchPath,
  imgSrcUrl,
}